import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Geometry } from "geojson";
import { useLayers } from "../../context/LayersContext";
import { useRulerModalContext, MAP_TOOLS } from "../../context/RulerContext";
import { useImportedLayers } from "../../context/ImportedLayersContext";
import {
  extractPrimaryGeometry,
  normalizeLatLng,
  isInsidePernambuco,
} from "../../utils/geo";
import {
  ApiGetSolarReport,
  ApiGetAreaDistribution,
  ApiGetDistanceToLayer,
  ApiGetAreaPotential,
  ApiGetWindAreaPotential,
  ApiGetWindSeries,
  ApiGetWindDensity,
  ApiGetWindDistribution,
  ApiGetWindPotential,
  ApiGetWindCumulative,
  ApiGetGenerationPowerSummary,
  ApiGetProjectionAreaStats,
  AreaStats,
  DistributionBin,
  DistanceToLayerResponse,
  StatCard,
  WindSeriesResponse,
  WindDensityResponse,
  WindDistributionResponse,
  WindPotentialResponse,
  WindCumulativeResponse,
  WindScenario,
  ApiGetWindRose,
  GenerationPowerSummaryResponse,
  ProjectionAreaStatsResponse,
} from "../../services/api";
import { WindRose } from "@/types/wind";
import { toast } from "sonner";
import { computeAreaKm2, patchAreaCards } from "../../utils/areaFormatting";
import { shouldClearCitySelection } from "./utils/drawingSelection";

interface SolarReportCache {
  layerName?: string;
  distribution?: DistributionBin[];
  stats?: AreaStats;
  potential?: StatCard[];
  windPotential?: StatCard[];
  windDensityDistribution?: DistributionBin[];
  windDensityAreaStats?: AreaStats;
  generationPowerSummary?: GenerationPowerSummaryResponse;
  distance?: {
    existing?: DistanceToLayerResponse;
    planned?: DistanceToLayerResponse;
    substation_existing?: DistanceToLayerResponse;
    substation_planned?: DistanceToLayerResponse;
  };
  distanceMode?: SystemMode;
}

export type SystemMode = "sin" | "distribuidora";

const GHI_LAYER_NAME = "aeroespacial:solar_ghi_200m_sirgas2000_anual";

export const WIND_DENSITY_LAYER_NAME = "pepe-raster:DENSIDADE_POTENCIA_100m";

export function useSelectedArea(
  systemMode: SystemMode = "sin",
  onCitySelectionCleared?: () => void
) {
  const { layers, layerTimeValues, setSelectedGeometry } = useLayers();
  const { drawings } = useRulerModalContext();
  const { importedLayers } = useImportedLayers();

  // Prevents duplicate concurrent API calls for the same drawing
  const fetchingDistanceIds = useRef<Set<string>>(new Set());

  // "Last request wins" token for fetchAreaAnalysis — incremented synchronously
  // at dispatch time so a stale response can be detected without depending on
  // React's render/effect cycle (a ref synced via useEffect isn't guaranteed
  // to be updated yet by the time an in-flight request's own .then() fires).
  const areaRequestTokenRef = useRef(0);

  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(
    null
  );
  const [selectedAreaDrawingId, setSelectedAreaDrawingId] = useState<
    string | null
  >(null);
  const [selectedImportedLayerId, setSelectedImportedLayerId] = useState<
    string | null
  >(null);
  const [selectedCityName, setSelectedCityName] = useState<string | null>(null);
  const [distributionData, setDistributionData] = useState<DistributionBin[]>(
    []
  );
  const [areaStats, setAreaStats] = useState<AreaStats | null>(null);
  const [areaPotential, setAreaPotential] = useState<StatCard[] | null>(null);
  const [areaWindPotential, setAreaWindPotential] = useState<StatCard[] | null>(
    null
  );
  const [distanceData, setDistanceData] = useState<{
    existing?: DistanceToLayerResponse;
    planned?: DistanceToLayerResponse;
    substation_existing?: DistanceToLayerResponse;
    substation_planned?: DistanceToLayerResponse;
  } | null>(null);
  const [isLoadingDistance, setIsLoadingDistance] = useState(false);
  const [monthlyData, setMonthlyData] = useState<number[]>([]);
  const [annualMean, setAnnualMean] = useState<number | undefined>(undefined);
  const [reportsCache, setReportsCache] = useState<
    Record<string, SolarReportCache>
  >({});
  const [showLegend, setShowLegend] = useState(false);

  const [windDensityDistributionData, setWindDensityDistributionData] =
    useState<DistributionBin[]>([]);
  const [windDensityAreaStats, setWindDensityAreaStats] =
    useState<AreaStats | null>(null);

  const [windHeight, setWindHeight] = useState("100m");
  const [windScenario, setWindScenario] = useState<WindScenario>(
    WindScenario.Teorico
  );

  /**
   * GeoJSON geometry of the currently selected area.
   * null → endpoints return state-wide data.
   */
  const [currentGeometry, setCurrentGeometry] = useState<Geometry | null>(null);

  const [windSeriesData, setWindSeriesData] =
    useState<WindSeriesResponse | null>(null);
  const [windDensityData, setWindDensityData] =
    useState<WindDensityResponse | null>(null);
  const [windDistributionData, setWindDistributionData] =
    useState<WindDistributionResponse | null>(null);
  const [windPotentialData, setWindPotentialData] =
    useState<WindPotentialResponse | null>(null);
  const [windCumulativeData, setWindCumulativeData] =
    useState<WindCumulativeResponse | null>(null);
  const [windRoseData, setWindRoseData] = useState<WindRose | null>(null);
  const [generationPowerSummary, setGenerationPowerSummary] =
    useState<GenerationPowerSummaryResponse | null>(null);
  const [selectedProjectionLayer, setSelectedProjectionLayer] = useState<
    string | null
  >(null);
  const [projectionAreaStats, setProjectionAreaStats] =
    useState<ProjectionAreaStatsResponse | null>(null);
  const [projectionPropitiousGeojson, setProjectionPropitiousGeojson] =
    useState<GeoJSON.Feature | null>(null);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);
  const [isLoadingWindPotential, setIsLoadingWindPotential] = useState(false);
  const [isLoadingPotential, setIsLoadingPotential] = useState(false);

  // Monthly wind averages for Pernambuco or the currently selected area
  useEffect(() => {
    const heightNum = parseInt(windHeight, 10);
    const geom = currentGeometry as {
      type: string;
      coordinates: number[][][];
    } | null;

    ApiGetWindSeries({ height: heightNum, geometry: geom })
      .then(setWindSeriesData)
      .catch(console.error);
    ApiGetWindDensity({ height: heightNum, geometry: geom })
      .then(setWindDensityData)
      .catch(console.error);
  }, [windHeight, currentGeometry]);

  // Area-specific wind data — re-fetched when height, scenario or geometry changes
  useEffect(() => {
    const numericHeight = parseInt(windHeight.replace(/\D/g, ""), 10);
    const layerName = `wind_speed_${numericHeight}m`;
    const geom = currentGeometry as {
      type: string;
      coordinates: number[][][];
    } | null;

    setIsLoadingWindPotential(true);
    Promise.all([
      ApiGetWindPotential({
        layer_name: layerName,
        height: numericHeight,
        geometry: geom,
        scenario: windScenario,
      }).then(setWindPotentialData),
      ApiGetWindCumulative({
        layer_name: layerName,
        height: numericHeight,
        geometry: geom,
        scenario: windScenario,
      }).then(setWindCumulativeData),
    ])
      .catch(console.error)
      .finally(() => setIsLoadingWindPotential(false));

    ApiGetWindDistribution({
      layer_name: "pepe-raster:windspeedtest",
      height: numericHeight,
      geometry: geom,
    })
      .then(setWindDistributionData)
      .catch(console.error);

    ApiGetWindRose({
      height: numericHeight,
      layer_name: layerName,
      geometry: geom,
      scenario: windScenario,
    })
      .then(setWindRoseData)
      .catch((error) => {
        toast("Error", { description: error?.message ?? "Erro desconhecido" });
      });
  }, [windHeight, windScenario, currentGeometry]);

  // Clear the projection stats whenever the projection type, the selected area or
  // the wind height changes — otherwise switching from one projection/area/height
  // to another would keep showing the previous numbers until "Calcular potencial"
  // is pressed. The height matters because it is sent as hub_height_m below and
  // the Memorial de Cálculo follows it live: leaving stale cards up would show
  // área/potência/energia for one height next to a memorial for another.
  // The actual calculation only runs on that button press (see
  // calculatePotential below), never automatically. Bumping the request id also
  // invalidates any in-flight fetchProjectionStats so a late response for the
  // previous selection can't repopulate the cards after this clear.
  const projectionRequestIdRef = useRef(0);
  useEffect(() => {
    projectionRequestIdRef.current += 1;
    setProjectionAreaStats(null);
    setProjectionPropitiousGeojson(null);
    // The bump above makes any in-flight request skip its own loading reset,
    // so reset it here — otherwise the button would stay on "Calculando…".
    setIsLoadingProjection(false);
  }, [selectedProjectionLayer, currentGeometry, windHeight]);

  const fetchProjectionStats = async () => {
    if (!selectedProjectionLayer) return;
    const requestId = ++projectionRequestIdRef.current;
    setProjectionAreaStats(null);
    setProjectionPropitiousGeojson(null);
    setIsLoadingProjection(true);
    const hubHeightM = parseInt(windHeight.replace(/\D/g, ""), 10) || undefined;
    try {
      const data = await ApiGetProjectionAreaStats({
        layer_name: selectedProjectionLayer,
        geometry: currentGeometry,
        hub_height_m: hubHeightM,
      });
      if (requestId !== projectionRequestIdRef.current) return;
      setProjectionAreaStats(data);
      // propitious_geometry is the exact shape propitious_area_km2 was measured
      // from — draw it on the map instead of the full state raster, which only
      // shows if enabled manually via Camadas → Projeções.
      setProjectionPropitiousGeojson(
        data.propitious_geometry
          ? { type: "Feature", properties: {}, geometry: data.propitious_geometry }
          : null
      );
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === projectionRequestIdRef.current) {
        setIsLoadingProjection(false);
      }
    }
  };

  const isGhiActive = layers.some((l) => l.toUpperCase().includes("GHI"));
  const activeSolarLayerName = GHI_LAYER_NAME;
  const isWindDensityActive = layers.some((l) =>
    l.toUpperCase().includes("DENSIDADE")
  );

  const fetchSolarData = useCallback(
    async (geometry: Geometry | null = null) => {
      try {
        const layerName = GHI_LAYER_NAME;
        const currentTimeStr = `${new Date().getFullYear() - 1}-01`;
        const parts = currentTimeStr.split("-");
        const year = parts.length === 2 ? parseInt(parts[0], 10) : 2025;

        const data = await ApiGetSolarReport({
          scenario_id: "clique_mapa",
          layer_name: layerName,
          year,
          geometry,
        });

        if (data.climate_data && data.climate_data.length > 0) {
          const solarVar = data.climate_data[0];
          setMonthlyData(solarVar.monthly_data);
          setAnnualMean(solarVar.mean);
        }
      } catch (error) {
        console.error("Erro ao buscar dados solares:", error);
      }
    },
    [layerTimeValues, layers]
  );

  useEffect(() => {
    fetchSolarData(currentGeometry);
  }, [fetchSolarData, currentGeometry]);

  const getDateToAreaDistribution = useCallback((): {
    year: number;
    month: number;
  } => {
    const currentTimeStr = "2025-01";
    const parts = currentTimeStr.split("-");
    const year = parts.length === 2 ? parseInt(parts[0], 10) : 2025;
    const month = parts.length === 2 ? parseInt(parts[1], 10) : 1;
    return { year, month };
  }, [layerTimeValues]);

  const fetchDefaultDistribution = useCallback(() => {
    const { year, month } = getDateToAreaDistribution();
    ApiGetAreaDistribution({
      layer_name: activeSolarLayerName,
      year,
      month,
      geometry: null,
    })
      .then((data) => {
        // Discard a stale state-wide response if the user selected an area
        // while this request was in flight — the area-specific fetch owns
        // the state from here.
        if (currentGeometryRef.current) return;
        if (data.distribution) {
          setDistributionData(data.distribution);
          setAreaStats(data.stats);
        }
      })
      .catch(console.error);
  }, [getDateToAreaDistribution, activeSolarLayerName]);

  // Fetch state-wide solar distribution when no area is selected
  useEffect(() => {
    if (currentGeometry) return;
    fetchDefaultDistribution();
  }, [currentGeometry, fetchDefaultDistribution]);

  const fetchDefaultWindDensityDistribution = useCallback(() => {
    const { year, month } = getDateToAreaDistribution();
    ApiGetAreaDistribution({
      layer_name: WIND_DENSITY_LAYER_NAME,
      year,
      month,
      geometry: null,
    })
      .then((data) => {
        // Discard a stale state-wide response if the user selected an area
        // while this request was in flight — the area-specific fetch owns
        // the state from here.
        if (currentGeometryRef.current) return;
        if (data.distribution) {
          setWindDensityDistributionData(data.distribution);
          setWindDensityAreaStats(data.stats);
        }
      })
      .catch(console.error);
  }, [getDateToAreaDistribution]);

  // Fetch state-wide wind density distribution when no area is selected —
  // mirrors the solar fetch above; independent of any active map layer.
  useEffect(() => {
    if (currentGeometry) return;
    fetchDefaultWindDensityDistribution();
  }, [currentGeometry, fetchDefaultWindDensityDistribution]);

  // Re-fetch distribution when the active solar layer changes while an area is selected
  useEffect(() => {
    if (!currentGeometry) return;
    const { year, month } = getDateToAreaDistribution();
    ApiGetAreaDistribution({
      layer_name: activeSolarLayerName,
      year,
      month,
      geometry: currentGeometry as any,
    })
      .then((data) => {
        if (data.distribution) {
          setDistributionData(data.distribution);
          setAreaStats(data.stats);
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSolarLayerName]);

  // Whether `drawingId` is still the active marking/imported layer/city selection —
  // used to discard responses for selections the user has since navigated away from.
  const isSelectionActive = (drawingId: string): boolean =>
    selectedDrawingIdRef.current === drawingId ||
    selectedImportedLayerIdRef.current === drawingId ||
    (selectedCityNameRef.current != null &&
      `city-${selectedCityNameRef.current}` === drawingId);

  const fetchDistanceData = async (
    geometry: any,
    drawingId: string,
    mode: SystemMode = systemMode
  ) => {
    if (fetchingDistanceIds.current.has(drawingId)) return;

    fetchingDistanceIds.current.add(drawingId);
    setIsLoadingDistance(true);
    try {
      const lineLayer =
        mode === "sin"
          ? "linhas_de_transmissao_base_existente"
          : "LTS-neoenergia";

      const substationLayer =
        mode === "sin" ? "subestacoes_base_existente" : "neoenergia_pe_untrat";

      const substationFilter =
        mode === "sin" ? "nome NOT LIKE '%PISF%'" : undefined;

      const [lineResult, substationResult] = await Promise.all([
        ApiGetDistanceToLayer({ geometry, layer_name: lineLayer }).catch(
          () => null
        ),
        ApiGetDistanceToLayer({
          geometry,
          layer_name: substationLayer,
          cql_filter: substationFilter,
        }).catch(() => null),
      ]);

      // Discard stale response if the user switched modes while this request was in flight
      if (systemModeRef.current !== mode) return;

      // If both calls failed, don't cache — a transient error should not permanently
      // hide infrastructure data on subsequent opens of the same drawing.
      if (lineResult === null && substationResult === null) return;

      const distancePayload = {
        existing: lineResult ?? undefined,
        substation_existing: substationResult ?? undefined,
      };

      // Cache the result for this drawing regardless of whether it's still selected,
      // but only update the visible distanceData if the user hasn't switched away —
      // otherwise a late response would overwrite the data for the new selection.
      setReportsCache((prev) => ({
        ...prev,
        [drawingId]: {
          ...(prev[drawingId] || {}),
          distance: distancePayload,
          distanceMode: mode,
        },
      }));
      if (isSelectionActive(drawingId)) {
        setDistanceData(distancePayload);
      }
    } catch (error) {
      console.error("Erro ao buscar distâncias:", error);
    } finally {
      fetchingDistanceIds.current.delete(drawingId);
      setIsLoadingDistance(false);
      // If systemMode changed while this request was in-flight, re-fetch for the current mode —
      // but only if drawingId is still the active selection (avoid cross-contaminating a new area).
      if (systemModeRef.current !== mode && isSelectionActive(drawingId)) {
        const geom = currentGeometryRef.current;
        if (geom) fetchDistanceData(geom, drawingId, systemModeRef.current);
      }
    }
  };

  const fetchAreaAnalysis = async (
    geometry: any,
    drawingId?: string,
    requestToken?: number
  ) => {
    // Callers that await something (e.g. fetchDistanceData) before reaching
    // this point must capture their own token synchronously, at the very
    // start of the selection change, and pass it in — otherwise two
    // selections racing on their distance fetch could bump this ref out of
    // user-selection order, letting an earlier selection's later-dispatched
    // requests look "current" (see handleAreaAnalysis/handleCityAnalysis).
    const token = requestToken ?? ++areaRequestTokenRef.current;
    const isStillCurrentRequest = () => areaRequestTokenRef.current === token;

    let firstPoint: number[] | null = null;
    if (geometry?.type === "Point") firstPoint = geometry.coordinates;
    else if (geometry?.type === "Polygon" && geometry.coordinates[0])
      firstPoint = geometry.coordinates[0][0];

    if (firstPoint) {
      const [lng, lat] = firstPoint;
      const { lat: nLat, lng: nLng } = normalizeLatLng(lat, lng);
      if (!isInsidePernambuco(nLat, nLng)) return;
    }

    // A call that's already stale by the time it starts (a newer selection
    // bumped the token while this one was still awaiting, e.g. a slow
    // fetchDistanceData before this function was ever reached) must not
    // clear the currently-displayed (newer) selection's state back to empty.
    if (isStillCurrentRequest()) {
      setDistributionData([]);
      setAreaStats(null);
      setGenerationPowerSummary(null);
      setWindDensityDistributionData([]);
      setWindDensityAreaStats(null);
    }

    const { year, month } = getDateToAreaDistribution();

    let drawingAreaKm2: number | undefined;
    if (geometry?.type === "Polygon" || geometry?.type === "MultiPolygon") {
      drawingAreaKm2 = computeAreaKm2(geometry);
    }

    const potentialPromise = Promise.all([
      ApiGetAreaPotential({ geometry }).catch(() => null),
      ApiGetWindAreaPotential({
        geometry,
        ...(isWindDensityActive && { layer_name: WIND_DENSITY_LAYER_NAME }),
      }).catch(() => null),
    ]).then(([solarData, windData]) => {
      const patchedSolarStats =
        solarData && drawingAreaKm2 !== undefined
          ? patchAreaCards(solarData.stats, drawingAreaKm2)
          : solarData?.stats;
      const patchedWindStats =
        windData && drawingAreaKm2 !== undefined
          ? patchAreaCards(windData.stats, drawingAreaKm2)
          : windData?.stats;
      if (drawingId) {
        setReportsCache((prev) => ({
          ...prev,
          [drawingId]: {
            ...(prev[drawingId] || {}),
            potential: patchedSolarStats,
            windPotential: patchedWindStats,
          },
        }));
      }
      // Discard a stale response for an area selection the user has since
      // navigated away from.
      if (isStillCurrentRequest()) {
        if (solarData && patchedSolarStats) {
          setAreaPotential(patchedSolarStats);
        }
        if (windData && patchedWindStats) setAreaWindPotential(patchedWindStats);
      }
    });

    const distributionPromise = ApiGetAreaDistribution({
      layer_name: activeSolarLayerName,
      year,
      month,
      geometry,
    })
      .then((data) => {
        if (data.distribution) {
          if (drawingId) {
            setReportsCache((prev) => ({
              ...prev,
              [drawingId]: {
                ...(prev[drawingId] || {}),
                layerName: activeSolarLayerName,
                distribution: data.distribution,
                stats: data.stats,
              },
            }));
          }
          // Discard a stale response for an area selection the user has
          // since navigated away from.
          if (isStillCurrentRequest()) {
            setDistributionData(data.distribution);
            setAreaStats(data.stats);
          }
        }
      })
      .catch(console.error);

    // Fetched independent of the active map layer (ATLAS-622) — the live panel's
    // carousel and the PDF report both need this data regardless of whether the
    // "Densidade" raster layer happens to be turned on.
    const windDensityPromise = ApiGetAreaDistribution({
      layer_name: WIND_DENSITY_LAYER_NAME,
      year,
      month,
      geometry,
    })
      .then((data) => {
        if (data.distribution) {
          if (drawingId) {
            setReportsCache((prev) => ({
              ...prev,
              [drawingId]: {
                ...(prev[drawingId] || {}),
                windDensityDistribution: data.distribution,
                windDensityAreaStats: data.stats,
              },
            }));
          }
          if (isStillCurrentRequest()) {
            setWindDensityDistributionData(data.distribution);
            setWindDensityAreaStats(data.stats);
          }
        }
      })
      .catch(console.error);

    const generationSummaryPromise = ApiGetGenerationPowerSummary(geometry)
      .then((data) => {
        if (drawingId) {
          setReportsCache((prev) => ({
            ...prev,
            [drawingId]: {
              ...(prev[drawingId] || {}),
              generationPowerSummary: data,
            },
          }));
        }
        // Discard a stale response for an area selection the user has since
        // navigated away from — this also covers city selections (no
        // drawingId), which isSelectionActive() cannot check on its own.
        if (isStillCurrentRequest()) {
          setGenerationPowerSummary(data);
        }
      })
      .catch(console.error);

    await Promise.allSettled([
      potentialPromise,
      distributionPromise,
      windDensityPromise,
      generationSummaryPromise,
    ]);
  };

  // Area/energy and distance are calculated on demand via calculatePotential() and
  // calculateDistance() (buttons in the panel), never automatically on selection.
  const handleAreaAnalysis = (geometry: Geometry, drawingId: string) => {
    // Invalidate any in-flight fetchAreaAnalysis for the area being replaced.
    areaRequestTokenRef.current += 1;
    setSelectedDrawingId(drawingId);
    setSelectedAreaDrawingId(drawingId);
    setSelectedGeometry(geometry);
    // Synchronous, unlike the ref-sync effect below — fetchDefault* guards read
    // this from a promise .then() that can settle before that effect commits.
    currentGeometryRef.current = geometry;
    setCurrentGeometry(geometry);
    clearAreaResults();
    setDistanceData(null);
  };

  const clearAreaState = () => {
    // Invalidate any in-flight fetchAreaAnalysis request for the area being
    // cleared — otherwise a late response could overwrite the state-wide
    // data this clears back to.
    areaRequestTokenRef.current += 1;
    setSelectedAreaDrawingId(null);
    setAreaPotential(null);
    setAreaWindPotential(null);
    setDistanceData(null);
    setSelectedGeometry(null);
    currentGeometryRef.current = null;
    setCurrentGeometry(null);
    setWindDensityDistributionData([]);
    setWindDensityAreaStats(null);
    setGenerationPowerSummary(null);
  };

  const clearAreaResults = () => {
    setDistributionData([]);
    setAreaStats(null);
    setAreaPotential(null);
    setAreaWindPotential(null);
    setGenerationPowerSummary(null);
    setWindDensityDistributionData([]);
    setWindDensityAreaStats(null);
  };

  const restoreAreaFromCache = (cachedData: SolarReportCache) => {
    setDistributionData(cachedData.distribution || []);
    setAreaStats(cachedData.stats || null);
    setAreaPotential(cachedData.potential ?? null);
    setAreaWindPotential(cachedData.windPotential ?? null);
    setGenerationPowerSummary(cachedData.generationPowerSummary ?? null);
    setWindDensityDistributionData(cachedData.windDensityDistribution || []);
    setWindDensityAreaStats(cachedData.windDensityAreaStats ?? null);
  };

  const handleDrawingSelect = (drawingId: string | null) => {
    setSelectedImportedLayerId(null);

    if (!drawingId) {
      setSelectedDrawingId(null);
      setSelectedCityName(null);
      onCitySelectionCleared?.();
      const wasGeometryNull = currentGeometry === null;
      clearAreaState();
      // If currentGeometry was already null, setCurrentGeometry(null) is a no-op
      // so the distribution useEffect won't re-fire — fetch PE data explicitly
      if (wasGeometryNull) {
        fetchDefaultDistribution();
        fetchDefaultWindDensityDistribution();
      }
      return;
    }

    setSelectedDrawingId(drawingId);

    const drawing = drawings.find((d) => d.id === drawingId);

    if (shouldClearCitySelection(drawingId, drawing)) {
      setSelectedCityName(null);
      onCitySelectionCleared?.();
    }

    if (!drawing || !drawing.layer) return;

    // Only MEASURE_AREA drives chart and area updates
    if (drawing.type !== MAP_TOOLS.MEASURE_AREA) return;

    setSelectedAreaDrawingId(drawingId);

    // @ts-ignore
    const geoJson = drawing.layer.toGeoJSON();
    const geometry = geoJson.geometry;

    setWindDistributionData(null);
    setSelectedGeometry(geometry);
    currentGeometryRef.current = geometry;
    setCurrentGeometry(geometry);

    // Invalidate any in-flight fetchAreaAnalysis for the drawing being
    // replaced, even on a cache hit below — a late response would otherwise
    // overwrite the restored cache.
    areaRequestTokenRef.current += 1;

    // Restore whatever's cached for this drawing; anything stale or missing is
    // cleared — press "Calcular potencial" / "Calcular distância" to refresh it.
    const cachedData = reportsCache[drawingId];
    const solarLayerMatches =
      cachedData && cachedData.layerName === activeSolarLayerName;
    const distanceModeMatches =
      cachedData && cachedData.distanceMode === systemMode;

    if (solarLayerMatches) {
      restoreAreaFromCache(cachedData);
    } else {
      clearAreaResults();
    }
    setDistanceData(distanceModeMatches ? cachedData.distance || null : null);
  };

  const handleImportedLayerSelect = (layerId: string | null) => {
    setSelectedImportedLayerId(layerId);
    setSelectedDrawingId(null);
    // Sem isso, calculatePotential/calculateDistance priorizariam o id do
    // desenho anterior e cacheariam o resultado da camada importada nele
    setSelectedAreaDrawingId(null);

    if (layerId) {
      setSelectedCityName(null);
      onCitySelectionCleared?.();
    }

    if (!layerId) {
      const wasGeometryNull = currentGeometry === null;
      clearAreaState();
      if (wasGeometryNull) {
        fetchDefaultDistribution();
        fetchDefaultWindDensityDistribution();
      }
      return;
    }

    const il = importedLayers.find((l) => l.id === layerId);
    if (!il) return;
    const geometry = extractPrimaryGeometry(il.geojson);
    if (!geometry) return;

    currentGeometryRef.current = geometry;
    setCurrentGeometry(geometry);

    // Invalidate any in-flight fetchAreaAnalysis for the layer being replaced.
    areaRequestTokenRef.current += 1;

    const cachedData = reportsCache[layerId];
    const solarLayerMatches =
      cachedData && cachedData.layerName === activeSolarLayerName;
    const distanceModeMatches =
      cachedData && cachedData.distanceMode === systemMode;

    if (solarLayerMatches) {
      restoreAreaFromCache(cachedData);
    } else {
      clearAreaResults();
    }
    setDistanceData(distanceModeMatches ? cachedData.distance || null : null);
  };

  const handleCityAnalysis = (geometry: any, cityName?: string) => {
    // Invalidate any in-flight fetchAreaAnalysis for the city being replaced.
    areaRequestTokenRef.current += 1;
    setSelectedCityName(cityName ?? null);
    setSelectedDrawingId(null);
    setSelectedAreaDrawingId(null);
    setSelectedImportedLayerId(null);
    setSelectedGeometry(geometry);
    currentGeometryRef.current = geometry;
    setCurrentGeometry(geometry);
    clearAreaResults();
    setDistanceData(null);
  };

  const handleCityDeselect = () => {
    // Invalidate any in-flight fetchAreaAnalysis request — see clearAreaState.
    areaRequestTokenRef.current += 1;
    setSelectedCityName(null);
    setAreaPotential(null);
    setAreaWindPotential(null);
    setDistanceData(null);
    setWindDensityDistributionData([]);
    setWindDensityAreaStats(null);
    const wasGeometryNull = currentGeometry === null;
    setSelectedGeometry(null);
    currentGeometryRef.current = null;
    setCurrentGeometry(null);
    // If currentGeometry was already null, setCurrentGeometry(null) is a no-op
    // so the distribution useEffect won't re-fire — fetch PE data explicitly
    if (wasGeometryNull) {
      fetchDefaultDistribution();
      fetchDefaultWindDensityDistribution();
    }
  };
  // Re-fetch distances when systemMode changes and there's an active geometry
  const currentGeometryRef = useRef<any>(null);
  const selectedDrawingIdRef = useRef<string | null>(null);
  const selectedImportedLayerIdRef = useRef<string | null>(null);
  const selectedCityNameRef = useRef<string | null>(null);
  const systemModeRef = useRef<SystemMode>(systemMode);
  useEffect(() => {
    // currentGeometryRef is synced synchronously at each setCurrentGeometry
    // call site instead — this effect runs too late for the guards that need it.
    selectedDrawingIdRef.current = selectedDrawingId;
    selectedImportedLayerIdRef.current = selectedImportedLayerId;
    selectedCityNameRef.current = selectedCityName;
    systemModeRef.current = systemMode;
  });

  // Distance is scoped to a mode (SIN vs. Distribuidora) — the cached value for the
  // previous mode doesn't apply, so clear it and let the user press "Calcular
  // distância" again rather than auto re-fetching.
  useEffect(() => {
    if (!currentGeometryRef.current) return;
    setDistanceData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemMode]);

  // Manual trigger for "Calcular potencial" — área propícia, potencial instalável e
  // energia anual, plus the projection stats when a projection is selected.
  const calculatePotential = async () => {
    if (!currentGeometry) return;
    setIsLoadingPotential(true);
    // O effect que observa [selectedProjectionLayer, currentGeometry] incrementa
    // o ref a cada troca de seleção/projeção — se mudou durante o fetchAreaAnalysis,
    // este closure está stale e não pode iniciar a projeção com a geometria antiga
    const projectionEpoch = projectionRequestIdRef.current;
    try {
      const drawingId = selectedAreaDrawingId ?? selectedImportedLayerId ?? undefined;
      await fetchAreaAnalysis(currentGeometry, drawingId);
      if (
        selectedProjectionLayer &&
        projectionRequestIdRef.current === projectionEpoch
      ) {
        await fetchProjectionStats();
      }
    } finally {
      setIsLoadingPotential(false);
    }
  };

  // Área da seleção atual medida no cliente — permite exibir a "Área Total" de
  // cidades/camadas importadas imediatamente, antes de "Calcular potencial"
  // (desenhos já têm a medição da régua; o backend substitui ambos após o cálculo).
  const selectionAreaKm2 = useMemo(() => {
    if (
      currentGeometry?.type !== "Polygon" &&
      currentGeometry?.type !== "MultiPolygon"
    )
      return null;
    try {
      return computeAreaKm2(currentGeometry);
    } catch {
      return null;
    }
  }, [currentGeometry]);

  // Manual trigger for "Calcular distância" — distance to transmission lines/substations.
  const calculateDistance = async () => {
    if (!currentGeometry) return;
    const drawingId =
      selectedAreaDrawingId ??
      selectedImportedLayerId ??
      (selectedCityName ? `city-${selectedCityName}` : null);
    if (!drawingId) return;
    await fetchDistanceData(currentGeometry, drawingId, systemMode);
  };

  return {
    selectedDrawingId,
    setSelectedDrawingId,
    selectedAreaDrawingId,
    selectionAreaKm2,
    selectedCityName,
    setSelectedCityName,
    distributionData,
    areaStats,
    areaPotential,
    areaWindPotential,
    distanceData,
    isLoadingDistance,
    monthlyData,
    annualMean,
    showLegend,
    setShowLegend,
    fetchSolarData,
    currentGeometry,
    handleDrawingSelect,
    handleImportedLayerSelect,
    handleAreaAnalysis,
    handleCityAnalysis,
    handleCityDeselect,
    windHeight,
    setWindHeight,
    windScenario,
    setWindScenario,
    windSeriesData,
    windDensityData,
    windDistributionData,
    windPotentialData,
    windCumulativeData,
    windRoseData,
    windDensityDistributionData,
    windDensityAreaStats,
    generationPowerSummary,
    selectedProjectionLayer,
    setSelectedProjectionLayer,
    projectionAreaStats,
    projectionPropitiousGeojson,
    isLoadingProjection,
    isLoadingWindPotential,
    calculatePotential,
    calculateDistance,
    isLoadingPotential,
  };
}
